import React, { useState } from 'react';
import { Plus, X, Calculator, AlertCircle } from 'lucide-react';

const QuantityDiscountManager = ({ 
  basePrice, 
  discounts = [], 
  onChange, 
  className = "" 
}) => {
  const [localDiscounts, setLocalDiscounts] = useState(
    discounts.length > 0 ? discounts : []
  );

  const handleAddDiscount = () => {
    const newDiscount = {
      minQuantity: 2,
      maxQuantity: null,
      pricePerUnit: basePrice * 0.9 // Default 10% discount
    };
    const updated = [...localDiscounts, newDiscount];
    setLocalDiscounts(updated);
    onChange(updated);
  };

  const handleRemoveDiscount = (index) => {
    const updated = localDiscounts.filter((_, i) => i !== index);
    setLocalDiscounts(updated);
    onChange(updated);
  };

  const handleDiscountChange = (index, field, value) => {
    const updated = localDiscounts.map((discount, i) => {
      if (i === index) {
        return {
          ...discount,
          [field]: field === 'pricePerUnit' ? parseFloat(value) || 0 : parseInt(value) || 0
        };
      }
      return discount;
    });
    setLocalDiscounts(updated);
    onChange(updated);
  };

  const calculateSavings = (pricePerUnit) => {
    const savings = basePrice - pricePerUnit;
    const percentage = basePrice > 0 ? (savings / basePrice * 100) : 0;
    return { savings, percentage };
  };

  const getDiscountPreview = () => {
    const preview = [
      { qty: 1, price: basePrice, total: basePrice, savings: 0 }
    ];
    
    localDiscounts
      .sort((a, b) => a.minQuantity - b.minQuantity)
      .forEach(discount => {
        const { savings } = calculateSavings(discount.pricePerUnit);
        preview.push({
          qty: discount.minQuantity,
          price: discount.pricePerUnit,
          total: discount.pricePerUnit * discount.minQuantity,
          savings: savings
        });
      });
    
    return preview;
  };

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h4 className="text-md font-medium text-gray-900">Sconti Quantità</h4>
          <button
            type="button"
            onClick={handleAddDiscount}
            className="flex items-center space-x-1 text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-md hover:bg-purple-200"
          >
            <Plus className="w-4 h-4" />
            <span>Aggiungi Sconto</span>
          </button>
        </div>

        {/* Info Box */}
        {basePrice && (
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex">
              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-xs text-blue-700">
                <p className="font-medium mb-1">Prezzo Base: €{basePrice.toFixed(2)}</p>
                <p>Configura i prezzi scontati per fasce di quantità crescenti</p>
              </div>
            </div>
          </div>
        )}

        {/* Discount Configuration */}
        <div className="space-y-3">
          {localDiscounts.map((discount, index) => {
            const { savings, percentage } = calculateSavings(discount.pricePerUnit);
            
            return (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    Fascia {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveDiscount(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Quantità Min
                    </label>
                    <input
                      type="number"
                      min="2"
                      value={discount.minQuantity}
                      onChange={(e) => handleDiscountChange(index, 'minQuantity', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Quantità Max
                    </label>
                    <input
                      type="number"
                      min={discount.minQuantity}
                      value={discount.maxQuantity || ''}
                      onChange={(e) => handleDiscountChange(index, 'maxQuantity', e.target.value)}
                      placeholder="Illimitata"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Prezzo Unitario €
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={basePrice}
                      value={discount.pricePerUnit}
                      onChange={(e) => handleDiscountChange(index, 'pricePerUnit', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                
                {/* Savings Display */}
                {savings > 0 && (
                  <div className="mt-2 text-xs text-green-600 bg-green-50 p-2 rounded">
                    <Calculator className="w-3 h-3 inline mr-1" />
                    Risparmio: €{savings.toFixed(2)} per bottiglia ({percentage.toFixed(1)}%)
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Preview Table */}
        {localDiscounts.length > 0 && basePrice && (
          <div className="mt-4">
            <h5 className="text-sm font-medium text-gray-900 mb-2">Anteprima Sconti</h5>
            <div className="overflow-hidden border border-gray-200 rounded-lg">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Quantità</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Prezzo/Btg</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Totale</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Risparmio</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getDiscountPreview().map((item, index) => (
                    <tr key={index} className={index === 0 ? 'bg-gray-50' : ''}>
                      <td className="px-3 py-2">{item.qty}</td>
                      <td className="px-3 py-2">€{item.price.toFixed(2)}</td>
                      <td className="px-3 py-2">€{item.total.toFixed(2)}</td>
                      <td className="px-3 py-2">
                        {item.savings > 0 ? (
                          <span className="text-green-600 font-medium">
                            -€{item.savings.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuantityDiscountManager;